import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Target, Gem, HandshakeIcon, Handshake } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Seo } from "@/components/Seo";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { BUSINESS } from "@/data/site";

export default function About() {
  return (
    <PublicLayout>
      <Seo title="About Us" description="Crawford Construction & Development Ltd. — a Belize-based construction company delivering quality craftsmanship, dependable service and on-time, on-budget projects for 12+ years." />
      <section className="pt-40 pb-24 bg-navy relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1516216628859-9bccecab13ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-navy/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase font-bold text-gold mb-5">About Us</p>
          <h1 className="font-display text-5xl sm:text-6xl text-white leading-tight max-w-3xl">
            {BUSINESS.years} years of building trust across Belize.
          </h1>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-16 items-center">
          <img src="https://images.unsplash.com/photo-1535892942118-1ce95182ae0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Crawford Construction project" className="w-full h-[480px] object-cover" />
          <div>
            <SectionHeading overline="Our Story" title="Crafted with precision. Built to endure." />
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Crawford Construction & Development Ltd. is a Belize-based construction company specializing in residential and commercial construction for the past {BUSINESS.years} years.
              </p>
              <p>
                We design and build concrete and wooden homes, waterfront structures, docks, piers, seawalls, retaining walls, land development projects, and custom construction solutions.
              </p>
              <p>
                Our focus is on quality craftsmanship, dependable service, and delivering projects on time and within budget. Whether it's a private home, vacation rental, or commercial development, we are committed to turning our clients' vision into reality with precision in every project.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {["Concrete & wooden homes", "Waterfront & docks", "Seawalls & retaining walls", "Land development"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-navy font-medium text-sm"><CheckCircle2 size={18} className="text-gold shrink-0" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-5">
          <SectionHeading overline="What Drives Us" title="Our commitment to you." center />
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { icon: Target, t: "Our Mission", d: "To deliver enduring, high-quality construction that turns our clients' vision into reality — every time." },
              { icon: Gem, t: "Our Standard", d: "Uncompromising craftsmanship and materials engineered for Belize's coastal environment." },
              { icon: Handshake, t: "Our Promise", d: "Dependable service, honest communication, and projects delivered on time and within budget." },
            ].map((v, i) => (
              <motion.div key={v.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border border-gray-100 p-8 hover:border-gold hover:shadow-lg transition-all">
                <v.icon size={30} className="text-gold" />
                <h3 className="font-display text-2xl text-navy mt-5">{v.t}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </PublicLayout>
  );
}
